/**
 * The side panel's small player (owner, 24 September 2026): the recording plays in the box its
 * poster stood in, with play and pause, the scrubber and its comment pins, and a comment on the
 * moment on screen. Everything else is the full player's, one button below.
 *
 * The `card` variant is the same player on a `/zadania` task card (owner, 25 September 2026): the
 * film and its controls on the left, what the task is about (`aside`) with the reply field and
 * „Analizuj" beside it. Its reply is about the WHOLE recording whether the film was started or not —
 * a card answers the client, it does not annotate a second — and the film opens where the client's
 * question is pinned (`startAt`).
 *
 * Built from the full player's own parts, never beside them: the `<video>` wiring and the clock
 * (`useClipPlayback`), the seen mark (`useClipSeen`), the thread's writes (`useClipDiscussion`), the
 * scrubber, the pins, the keyboard and the comment's moment. The film waits for the trainer: the
 * poster is what shows until the first play or seek, and showing it is not watching it.
 */

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';

import { clipAspect, formatClock, type ClipEntry } from '~/entities/exercise-clip';
import { useSessionUser } from '~/entities/session';
import { displayInitials } from '~/shared/lib/displayInitials';
import ghost from '~/shared/ui/ghostButton.module.css';
import photo from '~/shared/ui/photoBox.module.css';
import { CameraGlyph, PauseGlyph, PlayGlyph, SendGlyph } from '~/shared/ui/VideoGlyphs';

import { keyTargetOf } from '../lib/keyTarget';
import { useClipDiscussion } from '../lib/useClipDiscussion';
import { useClipPlayback, useVideoClock, type VideoClock } from '../lib/useClipPlayback';
import { useClipActivity, useClipSeen } from '../lib/useClipSeen';
import { commentOffsetMs, openingSecond, pinJump } from '../model/playback';
import { playerCommand, type PlayerCommand } from '../model/playerKeys';
import { commentPins } from '../model/playerView';

import player from './ClipPlayer.module.css';
import styles from './ClipMiniPlayer.module.css';
import { PlayerScrubber } from './PlayerScrubber';

export interface ClipMiniPlayerProps {
  readonly clientId: number;
  /** The panel's recording. Another one in its place starts afresh, from its poster. */
  readonly clip: ClipEntry;
  /** What the film is, for assistive technology: the movement's name. */
  readonly label: string;
  /** The full player is open over the page: the small one holds still under it. */
  readonly held: boolean;
  /** Open the full player, at the second the film was left on, or at the top. */
  readonly onOpen: (startAt: number | null) => void;
  /** The trainer saw or answered the clip; the host refreshes what it lists from that. */
  readonly onActivity: () => void;
  /** The door into the full player, which the host focuses when it swaps the recording. */
  readonly openRef?: Ref<HTMLButtonElement>;
  /** Where it stands: the Forma side panel (`dock`, the default) or a task card (`card`, see above). */
  readonly variant?: 'dock' | 'card';
  /** The second the film opens at, or `null` for the top. */
  readonly startAt?: number | null;
  /** What the card is about, drawn above the reply field. The dock has none. */
  readonly aside?: ReactNode;
  /** The reply field, for a host control that writes into it (the card's „Zostaw notatkę"). */
  readonly fieldRef?: RefObject<HTMLInputElement | null>;
  /** What the card draws under „Analizuj" — the movement's trend. The dock has none. */
  readonly footer?: ReactNode;
}

/** What belongs to the recording on screen: started or not, the draft, a refused send. */
interface MiniState {
  readonly clipId: number;
  readonly started: boolean;
  readonly draft: string;
  readonly failed: boolean;
}

const fresh = (clipId: number): MiniState => ({ clipId, started: false, draft: '', failed: false });

export function ClipMiniPlayer({
  clientId,
  clip,
  label,
  held,
  onOpen,
  onActivity,
  openRef,
  variant = 'dock',
  startAt = null,
  aside,
  fieldRef,
  footer,
}: ClipMiniPlayerProps) {
  const { t, i18n } = useTranslation();
  const user = useSessionUser();
  const [state, setState] = useState<MiniState>(() => fresh(clip.id));
  const shown = state.clipId === clip.id ? state : fresh(clip.id);
  if (state !== shown) setState(shown);
  const [filmFailed, setFilmFailed] = useState<number | null>(null);
  const ownField = useRef<HTMLInputElement>(null);
  const field = fieldRef ?? ownField;
  const card = variant === 'card';
  const playButton = useRef<HTMLButtonElement>(null);

  const refreshActivity = useClipActivity(onActivity);
  const markSeen = useClipSeen(clientId, refreshActivity);
  const playback = useClipPlayback(
    () => {
      if (shown.started) markSeen(clip.id, clip.exerciseId);
    },
    { autoplay: false, at: startAt },
  );
  const viewer = {
    userId: user?.id ?? null,
    fullName: user?.displayName ?? '',
    initials: displayInitials(user?.displayName ?? ''),
    avatarUrl: user?.avatarUrl ?? null,
  };
  const discussion = useClipDiscussion(
    clip.id,
    true,
    { clientId, exerciseId: clip.exerciseId },
    viewer,
    refreshActivity,
  );

  const { attach, clock, playing, loop, pause, togglePlay, seekTo, currentTime } = playback;
  if (playing && !shown.started) setState({ ...shown, started: true });

  useEffect(() => {
    if (held) pause();
  }, [held, pause]);

  const start = () => {
    if (!shown.started) setState({ ...shown, started: true });
  };
  const seek = (seconds: number) => {
    start();
    seekTo(seconds);
  };
  const pins = commentPins(clip.comments, viewer.userId, { t, locale: i18n.language });
  const duration = playback.duration > 0 ? playback.duration : (clip.media.durationSeconds ?? 0);

  const run = (command: PlayerCommand): boolean => {
    switch (command.kind) {
      case 'togglePlay':
        togglePlay();
        return true;
      case 'seekBy':
        seek(currentTime() + command.seconds);
        return true;
      case 'seekFraction':
        seek(command.fraction * duration);
        return true;
      case 'seekEdge':
        seek(command.to === 'start' ? 0 : duration);
        return true;
      case 'commentJump': {
        const target = pinJump(
          pins.map((pin) => pin.seconds),
          currentTime(),
          command.by,
        );
        if (target !== null) seek(target);
        return true;
      }
      case 'compose':
        pause();
        field.current?.focus();
        return true;
      default:
        // Speed, frames, the loop, zoom, compare and the rest are the full player's.
        return false;
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented) return;
    const command = playerCommand({
      key: event.key,
      shiftKey: event.shiftKey,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      altKey: event.altKey,
      target: keyTargetOf(event.target),
      zoomed: false,
    });
    if (command !== null && run(command)) event.preventDefault();
  };

  const send = () => {
    const body = shown.draft.trim();
    if (body === '') return;
    // The card's reply is about the whole recording (see the header); the dock's takes its second.
    const offset = card ? null : commentOffsetMs(shown.started, currentTime());
    setState({ ...shown, draft: '', failed: false });
    void discussion.post(clip.id, body, offset).then((ok) => {
      if (ok) return;
      // The words come back to the field, unless the trainer has started another comment there.
      setState((current) =>
        current.clipId === clip.id
          ? { ...current, draft: current.draft === '' ? body : current.draft, failed: true }
          : current,
      );
    });
  };

  const ratio = { '--clip-ratio': String(clipAspect(clip.media)) } as CSSProperties;
  const playLabel = t(playing ? 'web:clipPlayer.controls.pause' : 'web:clipPlayer.controls.play');

  return (
    <div
      aria-label={t('web:forma.dock.player')}
      className={`${String(styles.mini)} ${card ? String(styles.card) : ''}`}
      onKeyDown={onKeyDown}
      role="group"
    >
      <div className={styles.box} style={ratio}>
        <video
          aria-label={label}
          className={styles.video}
          key={clip.id}
          loop
          onError={() => {
            setFilmFailed(clip.id);
          }}
          onLoadedData={() => {
            setFilmFailed(null);
          }}
          playsInline
          poster={clip.media.posterUrl ?? undefined}
          preload="metadata"
          ref={attach}
          src={clip.media.videoUrl ?? undefined}
        />
        {/* A film opened at the client's question shows THAT frame, paused (owner, 26 September
          * 2026): the element seeks there once its metadata lands, so the poster stands aside. */}
        {shown.started || startAt !== null || clip.media.posterUrl === null ? null : (
          <img
            alt=""
            className={`${String(photo.photo)} ${String(styles.poster)}`}
            decoding="async"
            src={clip.media.posterUrl}
          />
        )}
        {/* The pointer's way in; the keyboard's is the control row's own play button. */}
        <button
          aria-label={playLabel}
          className={styles.surface}
          onClick={() => {
            start();
            togglePlay();
          }}
          tabIndex={-1}
          type="button"
        >
          {playing ? null : (
            <span className={styles.play}>
              <PlayGlyph className={styles.playGlyph} />
            </span>
          )}
        </button>
        {shown.started && filmFailed === clip.id ? (
          <div className={player.filmNotice}>
            <span role="alert">{t('web:clipPlayer.states.filmFailed')}</span>
          </div>
        ) : null}
      </div>

      <div className={styles.row}>
        <button
          aria-label={playLabel}
          className={player.icon}
          onClick={() => {
            start();
            togglePlay();
          }}
          ref={playButton}
          type="button"
        >
          {playing ? <PauseGlyph className={player.glyph} /> : <PlayGlyph className={player.glyph} />}
        </button>
        <div className={styles.scrubber}>
          <PlayerScrubber
            clock={clock}
            duration={duration}
            loop={loop}
            onSeek={seek}
            pins={pins}
            videoUrl={clip.media.videoUrl}
          />
        </div>
        <Readout clock={clock} duration={duration} locale={i18n.language} />
      </div>

      <div className={styles.side}>
      {aside}
      <div className={styles.composer}>
        <ComposerField
          clock={shown.started && !card ? clock : null}
          draft={shown.draft}
          fieldRef={field}
          onBackToFilm={() => {
            playButton.current?.focus();
          }}
          onDraft={(draft) => {
            setState({ ...shown, draft });
          }}
          onFocus={pause}
          onSend={send}
        />
        {card ? (
          <button className={`${String(ghost.ghost)} ${String(styles.send)}`} onClick={send} type="button">
            <SendGlyph className={player.glyphSmall} />
            {t('web:clipPlayer.discussion.send')}
          </button>
        ) : (
          <button
            aria-label={t('web:clipPlayer.discussion.send')}
            className={player.icon}
            onClick={send}
            type="button"
          >
            <SendGlyph className={player.glyphSmall} />
          </button>
        )}
      </div>
      {shown.failed ? (
        <p className={player.error} role="alert">
          {t('web:clipPlayer.discussion.sendFailed')}
        </p>
      ) : null}

      <button
        aria-label={t(card ? 'web:tasks.blocks.clip.analyseLabel' : 'web:forma.dock.openLabel')}
        className={`${String(ghost.ghost)} ${String(styles.open)}`}
        onClick={() => {
          pause();
          onOpen(openingSecond(shown.started, currentTime()));
        }}
        ref={openRef}
        type="button"
      >
        {card ? <CameraGlyph className={player.glyphSmall} /> : null}
        {t(card ? 'web:tasks.blocks.clip.analyse' : 'web:forma.dock.open')}
      </button>
      {footer}
      </div>
    </div>
  );
}

/** „0:12 / 0:45". It reads the clock itself, so a playing film re-renders this line alone. */
function Readout({
  clock,
  duration,
  locale,
}: {
  readonly clock: VideoClock | null;
  readonly duration: number;
  readonly locale: string;
}) {
  const now = useVideoClock(clock, (seconds) => formatClock(seconds, false, locale));
  return (
    <span className={styles.clock}>
      {now} / {formatClock(duration, false, locale)}
    </span>
  );
}

/**
 * The comment field. Its placeholder names the moment the comment will take, read from the clock
 * here; with no clock (the film not started) the comment is about the whole recording.
 */
function ComposerField({
  clock,
  draft,
  fieldRef,
  onDraft,
  onFocus,
  onSend,
  onBackToFilm,
}: {
  readonly clock: VideoClock | null;
  readonly draft: string;
  readonly fieldRef: Ref<HTMLInputElement>;
  readonly onDraft: (draft: string) => void;
  readonly onFocus: () => void;
  readonly onSend: () => void;
  readonly onBackToFilm: () => void;
}) {
  const { t, i18n } = useTranslation();
  const time = useVideoClock(clock, (seconds) => formatClock(seconds, false, i18n.language));
  return (
    <input
      aria-label={t('web:clipPlayer.discussion.field')}
      className={styles.field}
      onChange={(event) => {
        onDraft(event.target.value);
      }}
      onFocus={onFocus}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          onSend();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          onBackToFilm();
        }
      }}
      placeholder={
        clock === null ? t('web:forma.dock.commentWhole') : t('web:forma.dock.commentAt', { time })
      }
      ref={fieldRef}
      type="text"
      value={draft}
    />
  );
}
